import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import IconButton, {
  ICON_BUTTON_ICONS,
  ICON_BUTTON_TYPES
} from '../../../../components/buttons/IconButton';
import { useConfirmDialog } from '../../../../components/dialogs/ConfirmDialog';

/**
 * TD-2461 — "Bulk exclude" dropdown for the search-results header.
 *
 * Two directions over the current filter set:
 *   - Remove filtered           → exclude the matching set F
 *   - Keep matches, exclude rest → exclude the complement A\F
 *
 * Kept in its own component (rather than inlined in SearchResults) so the
 * `useConfirmDialog` hook only runs when the control is actually mounted. The
 * parent renders it exclusively when an effective filter is active, so
 * SearchResults stays renderable in tests that don't wrap a
 * ConfirmDialogProvider. Closing on outside-click is owned by the parent
 * (its store holds `isBulkExcludeMenuToggled`).
 */
const BulkExcludeControl = observer(({ store }) => {
  const { showConfirm } = useConfirmDialog();

  // Two distinct reasons "Keep matches" can be disabled, needing opposite copy:
  // nothing matches (no set to keep), or everything matches (no complement to
  // exclude). Extracted rather than nested inline to keep the ternary flat.
  const keepMatchesTooltip = () => {
    if (store.canKeepMatches) return '';
    if (store.keptCount === 0) return 'No companies match the current filters';
    return 'All loaded companies match the current filters';
  };

  const handleRemoveFiltered = async () => {
    store.toggleBulkExcludeMenu(false);
    if (!store.canRemoveFiltered) return;
    const n = store.keptCount;
    const confirmed = await showConfirm({
      title: 'Remove the filtered companies',
      message: `Exclude the ${n} compan${n === 1 ? 'y' : 'ies'} matching your filters? They move to "Excluded results" and can be restored.`,
      confirmText: `Exclude ${n}`,
      cancelText: 'Cancel',
      variant: 'danger'
    });
    if (confirmed) store.excludeMatching();
  };

  const handleKeepMatches = async () => {
    store.toggleBulkExcludeMenu(false);
    if (!store.canKeepMatches) return;
    const kept = store.keptCount;
    const drop = store.complementCount;
    const confirmed = await showConfirm({
      title: 'Keep matches, exclude the rest',
      message: `Keep the ${kept} matching compan${kept === 1 ? 'y' : 'ies'} and exclude the other ${drop}? Excluded companies move to "Excluded results" and can be restored.`,
      confirmText: `Exclude ${drop}`,
      cancelText: 'Cancel',
      variant: 'danger'
    });
    if (confirmed) store.excludeComplement();
  };

  return (
    <div className="bulk-exclude-control">
      <IconButton
        id="bulk-exclude-trigger"
        innerText="Bulk exclude"
        icon={
          store.isBulkExcludeMenuToggled
            ? ICON_BUTTON_ICONS.CHEVRON_UP
            : ICON_BUTTON_ICONS.CHEVRON_DOWN
        }
        iconOnRight
        type={ICON_BUTTON_TYPES.BLUE}
        disabled={store.isBulkExcluding}
        onClick={() => store.toggleBulkExcludeMenu()}
        filled={store.isBulkExcludeMenuToggled}
      />
      {store.isBulkExcludeMenuToggled && (
        <div className="bulk-exclude-menu-wrap">
          <IconButton
            hoverType={ICON_BUTTON_TYPES.BLUE}
            withBorder={false}
            innerText={`Remove filtered (${store.keptCount})`}
            tooltipText={store.canRemoveFiltered ? '' : 'No companies match the current filters'}
            disabled={!store.canRemoveFiltered}
            onClick={handleRemoveFiltered}
          />
          <IconButton
            hoverType={ICON_BUTTON_TYPES.BLUE}
            withBorder={false}
            innerText={`Keep matches, exclude the rest (${store.complementCount})`}
            tooltipText={keepMatchesTooltip()}
            disabled={!store.canKeepMatches}
            onClick={handleKeepMatches}
          />
        </div>
      )}
    </div>
  );
});

BulkExcludeControl.propTypes = {
  store: PropTypes.object.isRequired
};

export default BulkExcludeControl;
